import {ChangeDetectionStrategy, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {BehaviorSubject, catchError, EMPTY, Observable, Subject, takeUntil} from "rxjs";
import {BillingContractModel} from "@services/back-office/model/billingContractModel";
import {UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators} from "@angular/forms";
import {ConfigService} from "@config/config.service";
import {SharedService} from "@shared/shared.service";
import {TranslateService} from "@ngx-translate/core";
import {ToastrService} from "ngx-toastr";
import {select, Store} from "@ngrx/store";
import {Reducers} from "@app/reducers";
import {Actions, ofType} from "@ngrx/effects";
import {SaveFile} from "@shared/services/save-file";
import {BillingService} from "@shared/services/billing.service";
import dayjs from "dayjs";
import {ValidateIfObject} from "@shared/validators/if-object.validator";
import {BillingContractDataModel} from "@services/back-office/model/billingContractDataModel";
import {BillingContractOrganizationModel} from "@services/back-office/model/billingContractOrganizationModel";
import * as contractsActions from '@billing/ngrx/actions/contracts.actions';
import * as actsActions from '@billing/ngrx/actions/acts.actions';
import {PermissionsModel} from "@shared/models/permission.model";
import * as selectFromLogin from "@login/ngrx/login.selectors";
import {filter, map, take} from "rxjs/operators";
import {BillingContractContactModel} from "@services/back-office/model/billingContractContactModel";
import { HttpErrorResponse } from "@angular/common/http";
import {ConfirmationDialogComponent} from "@shared/components/confirmation-dialog/confirmation-dialog.component";
import {BillingContractDocumentRowModel} from "@services/back-office/model/billingContractDocumentRowModel";
import {PageBillingContractDocumentRowModel} from "@services/back-office/model/pageBillingContractDocumentRowModel";
import {ActDialogComponent} from "@billing/components/acts/act-dialog/act-dialog.component";
import {InvoiceDialogComponent} from "@billing/components/invoices/invoice-dialog/invoice-dialog.component";
import {PaymentDialogComponent} from "@billing/components/payments/payment-dialog/payment-dialog.component";
import {AddDocumentDialogComponent} from "@billing/components/documents/add-document-dialog/add-document-dialog.component";
import {ReverseActDialogComponent} from "@billing/components/documents/reverse-act-dialog/reverse-act-dialog.component";
import {RefundDialogComponent} from "@billing/components/payments/refund-dialog/refund-dialog.component";
import {ListDialogComponent} from "@shared/components/list-dialog/list-dialog.component";
import {ListItemType} from "@shared/enum/list-item-type";
import {PaginationModel} from "@shared/models/pagination.model";
import {ComponentType} from "@angular/cdk/portal";
import {BillingCounterpartyModel} from "@services/back-office/model/billingCounterpartyModel";
import {AssociationCalculationModel} from "@services/back-office/model/associationCalculationModel";
import {BillingContractDocumentModel} from "@services/back-office/model/billingContractDocumentModel";
// @ts-ignore
import * as fileSaver from "file-saver";
import {Base64} from "js-base64";
import {BillingDahCounterpartyModel} from "@services/back-office/model/billingDahCounterpartyModel";
import {AssociationListResponse} from "@services/back-office/model/associationListResponse";
import {NameResponse} from "@services/associations/model/nameResponse";
import {BillingDeletedActModel} from '@services/back-office/model/billingDeletedActModel';
import {MatTableDataSource} from '@angular/material/table';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';

@Component({
    selector: 'app-contract-dialog',
    templateUrl: './contract-dialog.component.html',
    styleUrls: ['./contract-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ContractDialogComponent implements OnInit, OnDestroy {
    /** Loading */
    loading$ = new BehaviorSubject(false);
    contractForm: UntypedFormGroup;
    contractDataForm: UntypedFormGroup;
    closeDialogFlag: boolean;
    totalAmount = 0;
    contractData: BillingContractDataModel[] = [];
    selectedContractData: { index: number, data: BillingContractDataModel };
    /** Permissions */
    permissions$: Observable<PermissionsModel[] | null>;
    permissions: PermissionsModel[] | null;
    addContactForm: UntypedFormGroup;
    addContactVisible = false;
    contactsColumns: string[] = ['name', 'comment', 'notifyTypes', 'actions'];
    contactsDataSource: MatTableDataSource<BillingContractContactModel> = new MatTableDataSource<BillingContractContactModel>();
    contactsLoaded: boolean;
    filesColumns: string[] = ['created', 'name', 'sign', 'actions'];
    filesDataSource: MatTableDataSource<BillingContractDocumentModel> = new MatTableDataSource<BillingContractDocumentModel>();
    filesLoaded: boolean;
    documentsColumns: string[] = ['name', 'amount', 'state', 'actions'];
    documentsDataSource: MatTableDataSource<BillingContractDocumentRowModel> = new MatTableDataSource<BillingContractDocumentRowModel>();
    documentsPagination: PaginationModel = {number: 0, size: new ConfigService().paginationConfig.pageSize, totalElements: 0};
    documentsLoaded: boolean;
    deletedActs: BillingDeletedActModel[];
    showDeletedActs: boolean;
    contractEditable: boolean;
    selectedAnnexDateForFile?: string;
    companies: BillingDahCounterpartyModel[] = [];
    selectedCompany: BillingDahCounterpartyModel;
    availableAmount: number;
    maximumYearAmount = 7800000;
    @ViewChild('fileInput') fileInput: ElementRef;
    @ViewChild('contractFileInput') contractFileInput: ElementRef;
    @ViewChild('annexFileInput') annexFileInput: ElementRef;
    /** Subject to destroy Subscriptions */
    private destroyed$ = new Subject<void>();
    @ViewChild('scrollableContent') private scrollableContent: ElementRef;

    public nameUnitKeys: string[] = [
        'PER_SQUARE_METER',
        'PER_PAN',
        'PER_MONTH',
        'PER_ACCRUAL_FLOW_ITEM',
        'PER_ACCRUAL_RESOURCE_FLOW_ITEM',
        'PER_EMPLOYEE'
    ];
    public nameUnitOptions: { key: string; label: string }[] = [];
    public filteredNames: Record<number, string[]> = {};

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
            contract?: BillingContractModel,
            association?: AssociationListResponse | NameResponse,
            counterparty?: BillingCounterpartyModel,
            type?: BillingDahCounterpartyModel.TypesEnum,
            /** Картка відкрита копіюванням договору: з даними оригіналу, але без id, тому номер беремо наступний */
            copy?: boolean
        },
        public dialogRef: MatDialogRef<ContractDialogComponent>,
        private formBuilder: UntypedFormBuilder,
        public config: ConfigService,
        private billingService: BillingService,
        private sharedService: SharedService,
        private translateService: TranslateService,
        private toastrService: ToastrService,
        private store$: Store<Reducers>,
        private actions$: Actions,
        private saveFile: SaveFile
    ) {
        this.permissions$ = this.store$.pipe(select(selectFromLogin.selectPermissions));
    }

    ngOnInit(): void {
        this.getPermissions();
        if (this.data.contract) {
            this.updateWidth();
            this.fillFormData();
            if (this.data.copy) {
                this.getNextNumber();
            }
        } else {
            this.getNextNumber();
            this.contractEditable = true;
            this.contractData.unshift(
                {
                    date: '',
                    editable: true,
                    organizations: []
                }
            );
            this.selectedContractData = {index: 0, data: this.contractData[0]};
        }

        if (!this.contractForm) {
            this.initForm();
        }

        if (!this.contractDataForm) {
            this.initFormArray();
        }

        // Копія отримує організацію одразу, тож розрахунок запитуємо так само, як після її вибору при створенні
        if (this.data.copy && this.data.contract?.organization?.id) {
            this.confirmGetCalculation(this.data.contract.organization.id, 0);
        }

        this.nameUnitOptions = this.nameUnitKeys.map(key => ({
            key,
            label: this.translateService.instant(
                `modules.billing.agreements.quantityTypes.${key}`
            )
        }));

        this.watchFailActions();
        this.watchAddSuccessAction();
        this.watchEditSuccessAction();
        this.watchDeleteSuccessAction();
        this.watchToggleActSignedStateSuccess();
        this.watchUpdateContractSuccess();
        this.getDahCompanies();
    }

    ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
        this.loading$.complete();
    }

    companySelected($event: any) {
        this.selectedCompany = $event.value;
        this.setAvailableAmount();
    }

    setAvailableAmount() {
        this.availableAmount = 0;
        if (this.selectedCompany?.amount) {
            const fullAmount = this.maximumYearAmount - this.selectedCompany.amount;
            const months = dayjs().endOf('year').diff(dayjs(), 'month');
            this.availableAmount = fullAmount / months;
        }
    }

    getNextNumber() {
        this.billingService.getNextContractNumber().pipe(
            take(1),
            map((res) => {
                if (!this.contractForm) {
                    this.initForm();
                }
                this.contractForm.get('number')?.setValue(res[this.data?.type ?? this.data?.contract?.type ?? 'SAAS']);
            })
        ).subscribe();
    }

    getDahCompanies() {
        this.billingService.getDahCompanies().pipe(
            take(1),
            map((resp: BillingDahCounterpartyModel[]) => {
                this.companies = resp;
                if (this.data.contract) {
                    // @ts-ignore
                    this.selectedCompany = resp.find(el => el.id == this.data.contract?.dahCounterParty?.id);
                } else {
                    this.companies = this.companies.filter(el => el.types?.has(this.data.type!));
                    this.selectedCompany = this.companies[0];
                }

                this.setAvailableAmount();
                this.contractForm.get('dahCounterParty')?.setValue(this.selectedCompany);
            }),
            catchError((error: HttpErrorResponse) => {
                if (error?.error?.message) {
                    SharedService.showErrorWithTitle(
                        this.translateService,
                        this.toastrService,
                        `responseErrors.${error.error.message}`
                    );
                }
                return EMPTY;
            })
        ).subscribe();
    }

    confirmGetCalculation(associationId: string, index: number, needUpdateArray?: boolean) {
        if (this.contractForm.get('type')?.value != 'SAAS') {
            return;
        }

        const controls = this.getControls();

        if (!associationId || !controls[index]) {
            return;
        }

        if (controls[index].get('price')?.value) {
            const confirmDialog$ = this.sharedService.openDialog(
                ConfirmationDialogComponent,
                {...this.config.confirmationDialog, hasBackdrop: true},
                {
                    title: SharedService.getTranslation(
                        this.translateService,
                        `Перезаповнити розрахункові дані?`,
                    ),
                    cancelText: 'common.buttons.no',
                    submitText: 'common.buttons.yes',
                }
            );

            confirmDialog$.afterClosed().pipe(
                take(1),
            ).subscribe((result) => {
                if (result) {
                    this.getCalculation(associationId, index);
                }
            });
        } else {
            this.getCalculation(associationId, index, needUpdateArray);
        }
    }

    getCalculation(associationId: string, index: number, needUpdateArray?: boolean) {
        this.billingService.getCalculation(associationId).pipe(
            takeUntil(this.destroyed$),
            map((res: AssociationCalculationModel) => {
                if (res) {
                    const controls = this.getControls();
                    controls[index].get('qty')!.setValue(res.qty);
                    controls[index].get('unit')!.setValue(res.unit);
                    controls[index].get('price')!.setValue(res.unit == 'PER_SQUARE_METER' ? '0.1' : '20');
                }

                if (!res && needUpdateArray) {
                    this.updateSelectedContractData();
                }
            })
        ).subscribe();
    }

    watchUpdateContractSuccess() {
        this.actions$.pipe(
            ofType(contractsActions.ContractsActionTypes.UpdateContractSuccess)
        ).subscribe((action: contractsActions.UpdateContractSuccess) => {
            if (action.payload) {
                this.data.contract = action.payload;
                this.fillFormData();
            }
        });
    }

    statusChanged(event: any) {
        if (event.value == 'CANCELLED') {
            this.contractForm.get('cancelDate')!.setValidators([Validators.required]);
            this.contractForm.get('cancelDate')!.updateValueAndValidity();
        } else {
            this.contractForm.get('cancelDate')!.reset();
            this.contractForm.get('cancelDate')!.clearValidators();
            this.contractForm.get('cancelDate')!.updateValueAndValidity();
        }
    }

    fillFormData() {
        this.contractData = this.data.contract!.data!;
        this.selectedContractData = {index: 0, data: this.contractData[0]};
        this.contractEditable = !this.contractData.find(el => !el.editable);

        this.patchData();
        this.patchFromArray();

        this.updateEditable();
        this.setPayerTypeDisable();
    }

    updateEditable() {
        if (this.contractEditable) {
            this.contractForm.get('dahCounterParty')?.enable();
            this.contractForm.get('contractDate')?.enable();
            this.contractForm.get('number')?.enable();
            this.contractForm.get('type')?.enable();
            this.contractForm.get('payerType')?.enable();
        } else {
            this.contractForm.get('dahCounterParty')?.disable();
            this.contractForm.get('contractDate')?.disable();
            this.contractForm.get('number')?.disable();
            this.contractForm.get('type')?.disable();
            this.contractForm.get('payerType')?.disable();
        }
    }

    setPayerTypeDisable() {
        if (this.isBusinessType()) {
            this.contractForm.get('payerType')?.setValue('COUNTERPARTY');
            this.contractForm.get('payerType')?.disable();
        } else if(this.contractEditable) {
            this.contractForm.get('payerType')?.enable();
        }
    }

    getPermissions() {
        this.permissions$.pipe(
            filter(data => !!data),
            take(1)
        ).subscribe((permissions: PermissionsModel[] | null) => {
                this.permissions = permissions;
            }
        );
    }

    checkPermissions(permission: string): boolean {
        return this.permissions ?
            SharedService.checkPermission(permission, this.permissions[0].permissions, this.permissions[0].permissionsExists) :
            false;
    }

    updateWidth() {
        this.dialogRef.updateSize(this.config.rightSideDialogProps.width, this.config.rightSideDialogProps.height);
    }

    initForm() {
        this.contractForm = this.formBuilder.group({
            contractDate: [{value: new Date(), disabled: !this.contractEditable}, [Validators.required]],
            cancelDate: ['', this.data?.contract?.status == 'CANCELLED' ? [Validators.required] : []],
            number: [{value: '', disabled: !this.contractEditable}, [Validators.required]],
            type: [{value: this.data?.type ? this.data.type : '', disabled: true}, [Validators.required]],
            status: ['CREATED', [Validators.required]],
            dahCounterParty: ['', [Validators.required]],
            payerType: [{value: (this.data.counterparty || this.isBusinessType()) ? 'COUNTERPARTY' : 'ASSOCIATION', disabled: !this.contractEditable || this.data.association || this.data.counterparty || this.isBusinessType()}],
            actPerMonths: [1, [Validators.required]],
            organization: [{value: this.data.association ? this.data.association : '', disabled: !!this.data.association}],
            counterParty: [{value: this.data.counterparty ? this.data.counterparty : '', disabled: !!this.data.counterparty}]
        });
    }

    // <editor-fold desc="Contract data List">

    addContractData(i?: number) {
        if (i) {
            const contractData = [...this.contractData];
            contractData.unshift({...this.contractData[i], date: '', comment: '', editable: true});
            this.contractData = contractData;
        } else {
            const contractData = [...this.contractData];
            contractData.unshift({...this.contractData[0], date: '', comment: '', editable: true});
            this.contractData = contractData;
        }
        this.selectedContractData = {index: 0, data: this.contractData[0]};
        this.patchFromArray();
    }

    selectContractData(i: number) {
        this.selectedContractData = {index: i, data: this.contractData[i]};
        this.patchFromArray();
    }

    deleteContractData(index: number) {
        this.contractData.splice(index, 1);
        if (this.selectedContractData.index == index) {
            this.selectContractData(0);
        }
    }

    onNameSelected(label: string, index: number, isUserInput: boolean) {
        if (!isUserInput) return;

        const option = this.nameUnitOptions
            .find(o => o.label === label);

        const controls = this.getControls();

        if (!option || !controls[index]) return;

        controls[index].get('unit')?.setValue(option.key);

        controls[index].get('name')?.setValue(option.label);

        this.updateSelectedContractData();
    }

    onNameInput(index: number) {
        const control = this.getControls()[index].get('name');
        const value = (control?.value || '').toLowerCase();

        this.filteredNames[index] = this.nameUnitOptions
            .filter(opt => opt.label.toLowerCase().includes(value))
            .map(opt => opt.label);
    }

    onNameBlur(index: number) {
        const control = this.getControls()[index].get('name');
        const value = control?.value;

        const match = this.nameUnitOptions
            .find(o => o.label === value);

        const unitControl = this.getControls()[index].get('unit');

        unitControl?.setValue(match?.key || 'SERVICE');

        this.updateSelectedContractData();
    }



    updateSelectedContractData(date?: boolean) {
        const contractDataForm = this.contractDataForm.getRawValue();
        const newDate = dayjs(contractDataForm.date).format('YYYY-MM-DD');

        if (date) {
            for (let i = 0; i < this.contractData?.length; i++) {
                if (this.contractData[i].date == newDate) {
                    this.toastrService.warning('Додаток з таким самим початком дії вже існує', 'Оберіть інший період');
                    this.contractDataForm.get('date')?.reset();
                    return;
                }
            }
        }

        const organizations: BillingContractOrganizationModel[] =
            contractDataForm &&
            contractDataForm.contractDataFormControls &&
            contractDataForm.contractDataFormControls.length ?
                contractDataForm.contractDataFormControls.map((item: any) => {
                    return {
                        ...item.organization && {organization: item.organization},
                        ...item.name && {name: item.name},
                        ...(item.qty || item.qty === 0) && {qty: item.qty},
                        ...item.unit && {unit: item.unit},
                        ...(item.price || item.price === 0) && {price: item.price},
                        ...(item.amount || item.amount === 0) && {amount: item.amount},
                    };
                }) :
                [];

        this.selectedContractData.data = {
            ...contractDataForm.date && {date: newDate},
            ...contractDataForm.comment && {comment: contractDataForm.comment},
            singleService: contractDataForm.singleService,
            organizations: organizations,
            editable: true
        };

        const contractData = [...this.contractData];
        contractData.splice(this.selectedContractData.index, 1, this.selectedContractData.data);
        this.contractData = contractData;
    }

    // </editor-fold>

    // <editor-fold desc="Form array">

    initFormArray() {
        this.contractDataForm = this.formBuilder.group({
            date: ['', [Validators.required]],
            comment: [''],
            singleService: [false],
            contractDataFormControls: this.formBuilder.array([this.initSetControls()])
        });

        if (this.data.association) {
            this.confirmGetCalculation(this.data.association.id!, 0, true);
        }
    }

    initSetControls() {
        const disabled = !this.selectedContractData.data.editable;
        return this.formBuilder.group({
            organization: [{value: '', disabled: disabled}],
            name: [{value: '', disabled: disabled}],
            qty: [{
                value: '',
                disabled: disabled
            }, [Validators.required, Validators.pattern(this.config.regex.moneyThreeDigits)]],
            unit: [{
                value: '',
                disabled: disabled
            }, [Validators.required]],
            price: [{value: '', disabled: disabled}, [Validators.required, Validators.pattern(this.config.regex.moneyTwoDigits)]],
            amount: [{value: '', disabled: disabled}, [Validators.required, Validators.pattern(this.config.regex.moneyTwoDigits)]],
        });
    }

    payerTypeChanged(event: any) {
        const dataFormControls = this.getControls();
        if (dataFormControls.length > 1) {
            for (let i = dataFormControls.length; i > 0; i--) {
                this.deleteFormArrayItem(i);
            }

            this.updateSelectedContractData();
        }

        if (event.value == 'ASSOCIATION') {
            this.contractForm.get('organization')!.setValidators([Validators.required, ValidateIfObject]);
            this.contractForm.get('organization')!.updateValueAndValidity();

            this.contractForm.get('counterParty')!.reset();
            this.contractForm.get('counterParty')!.clearValidators();
            this.contractForm.get('counterParty')!.updateValueAndValidity();
        } else {
            this.contractForm.get('counterParty')!.setValidators([Validators.required, ValidateIfObject]);
            this.contractForm.get('counterParty')!.updateValueAndValidity();

            this.contractForm.get('organization')!.reset();
            this.contractForm.get('organization')!.clearValidators();
            this.contractForm.get('organization')!.updateValueAndValidity();
        }
    }

    getControls() {
        return (this.contractDataForm.get('contractDataFormControls') as UntypedFormArray).controls;
    }

    addFormArrayItem() {
        (this.contractDataForm.controls['contractDataFormControls'] as UntypedFormArray).push(this.initSetControls());
        setTimeout(() => {
            this.scrollToBottom();
        }, 50);
    }

    getFormArrayControlsQuantity() {
        return (this.contractDataForm.get('contractDataFormControls') as UntypedFormArray).controls.length;
    }

    deleteFormArrayItem(key: any) {
        if (this.getFormArrayControlsQuantity() > 1) {
            (this.contractDataForm.controls['contractDataFormControls'] as UntypedFormArray).removeAt(key);
        } else if (this.getFormArrayControlsQuantity() === 1) {
            this.contractDataForm.reset();
        }

        this.updateSelectedContractData();
    }

    scrollToBottom() {
        this.scrollableContent.nativeElement.scrollTop = this.scrollableContent.nativeElement.scrollHeight;
    }

    countAmount(key: any) {
        const quantity = (this.contractDataForm.get('contractDataFormControls') as UntypedFormArray).controls[key].get('qty')?.value;
        const price = (this.contractDataForm.get('contractDataFormControls') as UntypedFormArray).controls[key].get('price')?.value;
        const controls = this.getControls();
        if (controls[key]) {
            controls[key].get('amount')!.setValue(Math.floor(quantity * price));
        }
    }

    getTotal() {
        this.totalAmount = 0;
        for (const key in (this.contractDataForm.get('contractDataFormControls') as UntypedFormArray).controls) {
            const amount = (this.contractDataForm.get('contractDataFormControls') as UntypedFormArray).controls[key].get('amount')?.value;
            if (amount) {
                this.totalAmount = Math.floor(this.totalAmount + amount);
            }
        }
    }

    // </editor-fold>

    // <editor-fold desc="Contact File">

    downloadContractTemplate() {
        if (!this.data.contract?.id) {
            return;
        }
        this.loading$.next(true);
        const documentName = SharedService.getTranslation(this.translateService, 'modules.billing.agreements.agreementDialog.document.defaultTemplate');
        this.billingService.downloadContractTemplate(this.data.contract.id).pipe(
            takeUntil(this.destroyed$),
            map(file => {
                this.saveFile.docFile(file, documentName);
                this.loading$.next(false);
            }),
            catchError((error: HttpErrorResponse) => {
                const reader = new FileReader();
                reader.addEventListener('loadend', (e) => {
                    if (typeof e.target?.result === 'string') {
                        const rawResult = JSON.parse(e.target.result);
                        if (rawResult && rawResult.message) {
                            SharedService.showErrorWithTitle(
                                this.translateService,
                                this.toastrService,
                                `responseErrors.${rawResult.message}`
                            );
                        }
                    }
                });
                reader.readAsText(error.error);
                this.loading$.next(false);
                return EMPTY;
            })
        ).subscribe();
    }

    uploadContractTemplate(event: any) {
        const file: File = event.target && event.target.files && event.target.files.length ? event.target.files[0] : null;

        if (file) {
            const formData: FormData = new FormData();
            formData.append('file', file, file.name);

            this.loading$.next(true);

            this.billingService.uploadContractTemplate(this.data.contract!.id!, formData).pipe(
                takeUntil(this.destroyed$),
                map(() => {
                    this.loading$.next(false);
                    this.fileInput.nativeElement.value = '';
                }),
                catchError((error: HttpErrorResponse) => {
                    this.loading$.next(false);
                    SharedService.showErrorWithTitle(
                        this.translateService,
                        this.toastrService,
                        `responseErrors.${error.error.message}`
                    );
                    this.fileInput.nativeElement.value = '';
                    this.loading$.next(false);
                    return EMPTY;
                })
            ).subscribe();
        }
    }

    clearContractTemplate() {
        const confirmDialog$ = this.sharedService.openDialog(
            ConfirmationDialogComponent,
            {...this.config.confirmationDialog, hasBackdrop: true},
            {
                title: SharedService.getTranslation(
                    this.translateService,
                    `Встановити стандартний шаблон для цього договору?`,
                ),
                cancelText: 'common.buttons.no',
                submitText: 'common.buttons.yes',
            }
        );

        confirmDialog$.afterClosed().pipe(
            take(1),
        ).subscribe((result) => {
            if (result) {
                this.loading$.next(true);
                this.billingService.setDefaultContractTemplate(this.data.contract!.id!).pipe(
                    takeUntil(this.destroyed$),
                    map(() => {
                        this.loading$.next(false);
                    }),
                    catchError((error: HttpErrorResponse) => {
                        this.loading$.next(false);
                        SharedService.showErrorWithTitle(
                            this.translateService,
                            this.toastrService,
                            `responseErrors.${error.error.message}`
                        );
                        this.loading$.next(false);
                        return EMPTY;
                    })
                ).subscribe();
            }
        });
    }

    uploadContractFile(event: any, type: string) {
        if (!this.contractData[0].date && !this.selectedAnnexDateForFile) {
            this.toastrService.warning('У крайньому додатку до договору не заповнено всі дані');
            return;
        }

        const file: File = event.target && event.target.files && event.target.files.length ? event.target.files[0] : null;

        if (file) {
            const formData: FormData = new FormData();
            formData.append('file', file, file.name);

            this.loading$.next(true);

            this.billingService.uploadContractFile(this.data.contract!.id!, formData, type, this.selectedAnnexDateForFile ? this.selectedAnnexDateForFile : this.contractData[0].date!).pipe(
                takeUntil(this.destroyed$),
                map(() => {
                    this.contractFileInput.nativeElement.value = '';
                    this.annexFileInput.nativeElement.value = '';
                    delete this.selectedAnnexDateForFile;
                    this.loading$.next(false);
                    this.getFiles();
                }),
                catchError((error: HttpErrorResponse) => {
                    this.loading$.next(false);
                    SharedService.showErrorWithTitle(
                        this.translateService,
                        this.toastrService,
                        `responseErrors.${error.error.message}`
                    );
                    this.contractFileInput.nativeElement.value = '';
                    this.annexFileInput.nativeElement.value = '';
                    delete this.selectedAnnexDateForFile;
                    this.loading$.next(false);
                    return EMPTY;
                })
            ).subscribe();
        }
    }

    generateContractFile(type: string, date?: string) {
        if (!this.contractData[0].date && !date) {
            this.toastrService.warning('У крайньому додатку до договору не заповнено всі дані');
            return;
        }

        this.loading$.next(true);

        this.billingService.generateContractFile(this.data.contract!.id!, type, date ? date : this.contractData[0].date!).pipe(
            takeUntil(this.destroyed$),
            map(() => {
                this.loading$.next(false);
                this.contractFileInput.nativeElement.value = '';
                this.getFiles();
            }),
            catchError((error: HttpErrorResponse) => {
                this.loading$.next(false);
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.error.message}`
                );
                this.contractFileInput.nativeElement.value = '';
                this.loading$.next(false);
                return EMPTY;
            })
        ).subscribe();
    }

    downloadContractFile(element: BillingContractDocumentModel, original: boolean) {
        this.loading$.next(true);
        this.billingService.downloadContractFile(element.id!, original).pipe(
            takeUntil(this.destroyed$),
            map(res => {
                const disposition: string = <string>res.headers.get('X-DAH-Content-Disposition');
                fileSaver.saveAs(new Blob([<ArrayBuffer>res.body], {type: <string>res.headers.get('Content-Type')}), <string>Base64.decode(disposition));
                this.loading$.next(false);
            }),
            catchError(() => {
                this.loading$.next(false);
                return EMPTY;
            })
        ).subscribe();
    }

    signContractFile(element: BillingContractDocumentModel) {
        this.loading$.next(true);
        this.billingService.signContractFile(element.id!).pipe(
            takeUntil(this.destroyed$),
            map(() => {
                this.getFiles();
                this.loading$.next(false);
            }),
            catchError(() => {
                this.loading$.next(false);
                return EMPTY;
            })
        ).subscribe();
    }

    deleteContractFile(element: BillingContractDocumentModel) {
        const confirmDialog$ = this.sharedService.openDialog(
            ConfirmationDialogComponent,
            {...this.config.confirmationDialog, hasBackdrop: true},
            {
                title: SharedService.getTranslation(
                    this.translateService,
                    `Файл договору буде видалено для нас та для клієнта, навіть якщо його вже підписали цифровим підписом. Продовжити?`,
                ),
                cancelText: 'common.buttons.no',
                submitText: 'common.buttons.yes',
            }
        );

        confirmDialog$.afterClosed().pipe(
            take(1),
        ).subscribe((result) => {
            if (result) {
                this.billingService.deleteContractFile(element.id!).pipe(
                    takeUntil(this.destroyed$),
                    map(() => {
                        this.loading$.next(false);
                        this.contractFileInput.nativeElement.value = '';
                        this.getFiles();
                    }),
                    catchError((error: HttpErrorResponse) => {
                        this.loading$.next(false);
                        SharedService.showErrorWithTitle(
                            this.translateService,
                            this.toastrService,
                            `responseErrors.${error.error.message}`
                        );
                        this.contractFileInput.nativeElement.value = '';
                        this.loading$.next(false);
                        return EMPTY;
                    })
                ).subscribe();
            }
        });
    }

    // </editor-fold>

    isBusinessType(): boolean {
        const type = this.contractForm?.get('type')?.value || this.data.type;

        return type === 'BUSINESS_SAAS' || type === 'BUSINESS_ACCOUNTING';
    }

    // <editor-fold desc="Patch and Submit">

    patchData() {
        // @ts-ignore
        this.contractData = this.data.contract.data;
        if (!this.contractForm) {
            this.initForm();
        }

        this.contractForm.patchValue({
            dahCounterParty: this.data.contract?.dahCounterParty,
            contractDate: this.data.contract?.date ? dayjs(this.data.contract.date).toDate() : '',
            cancelDate: this.data.contract?.cancelDate ? dayjs(this.data.contract.cancelDate).toDate() : '',
            number: this.data.contract?.number,
            status: this.data.contract?.status,
            type: this.data.contract?.type,
            payerType: this.data.contract?.counterParty || this.isBusinessType() ? 'COUNTERPARTY' : 'ASSOCIATION',
            ...this.data.contract?.organization && {organization: this.data.contract.organization},
            ...this.data.contract?.counterParty && {counterParty: this.data.contract.counterParty},
            actPerMonths: this.data.contract?.actPerMonths
        });
    }

    patchFromArray() {
        const selectedContractDataList = [...this.selectedContractData.data.organizations!];
        this.initFormArray();

        this.contractDataForm.get('date')?.setValue(this.selectedContractData.data.date ? dayjs(this.selectedContractData.data.date) : '');
        this.contractDataForm.get('singleService')?.setValue(this.selectedContractData.data.singleService ? this.selectedContractData.data.singleService : false);
        this.contractDataForm.get('comment')?.setValue(this.selectedContractData.data.comment ? this.selectedContractData.data.comment : '');

        if (this.selectedContractData.data.editable) {
            this.contractDataForm.get('comment')?.enable();
        } else {
            this.contractDataForm.get('comment')?.disable();
        }

        this.initSetControls();
        // @ts-ignore
        const difference = selectedContractDataList.length - this.getFormArrayControlsQuantity();
        if (difference > 0) {
            for (let i = 0; i < difference; i++) {
                this.addFormArrayItem();
            }
        }

        const controls = this.getControls();

        for (let i = 0; i < selectedContractDataList.length; i++) {
            const contractData: BillingContractOrganizationModel = selectedContractDataList[i];
            if (controls[i]) {
                controls[i].get('organization')!.setValue(contractData.organization ? contractData.organization : '');
                controls[i].get('qty')!.setValue((contractData.qty || contractData.qty === 0) ? contractData.qty : '');
                controls[i].get('unit')!.setValue(contractData.unit ? contractData.unit : '');
                controls[i].get('price')!.setValue((contractData.price || contractData.price === 0) ? contractData.price : '');
                controls[i].get('amount')!.setValue((contractData.amount || contractData.amount === 0) ? contractData.amount : '');

                const unitKey = contractData.unit;
                let nameValue = contractData.name;

                if (
                    !nameValue &&
                    unitKey &&
                    (this.contractForm.get('type')?.value === 'ACCOUNTING' ||
                        this.contractForm.get('type')?.value === 'BUSINESS_ACCOUNTING')
                ) {
                    nameValue = this.translateService.instant(
                        `modules.billing.agreements.quantityTypes.${unitKey}`
                    );
                }

                controls[i].get('name')!.setValue(nameValue || '');
            }
        }

        this.getTotal();
    }

    private resolveName(name: string | null, unit: string | null): string {
        if (name) return name;
        if (unit) {
            return this.translateService.instant(
                `modules.billing.agreements.quantityTypes.${unit}`
            );
        }
        return '';
    }

    submitForm(closeDialog: boolean) {
        this.closeDialogFlag = closeDialog;
        this.loading$.next(true);
        const formData = this.contractForm.getRawValue();

        const request: BillingContractModel = {
            date: dayjs(formData.contractDate).format('YYYY-MM-DD'),
            ...formData.status == 'CANCELLED' && {cancelDate: dayjs(formData.cancelDate).format('YYYY-MM-DD')},
            number: formData.number,
            status: formData.status,
            dahCounterParty: formData.dahCounterParty,
            type: formData.type,
            ...formData && formData.organization && {organization: formData.organization},
            ...formData && formData.counterParty && {counterParty: formData.counterParty},
            actPerMonths: formData.actPerMonths,
            data: this.contractData
        };

        if (this.data?.contract?.id) {
            this.store$.dispatch(new contractsActions.EditContracts({...request, id: this.data.contract.id}));
        } else {
            this.store$.dispatch(new contractsActions.AddContracts(request));
        }
    }

    // </editor-fold>

    watchAddSuccessAction() {
        this.actions$.pipe(
            ofType(contractsActions.ContractsActionTypes.AddContractsSuccess),
            takeUntil(this.destroyed$)
        ).subscribe((action: contractsActions.AddContractsSuccess) => {
            if (this.closeDialogFlag) {
                this.closeDialog(true);
            } else {
                this.data.contract = {...this.data.contract, id: action.payload.id};
                this.loading$.next(false);
            }
        });
    }

    watchEditSuccessAction() {
        this.actions$.pipe(
            ofType(contractsActions.ContractsActionTypes.EditContractsSuccess),
            takeUntil(this.destroyed$)
        ).subscribe((action: contractsActions.EditContractsSuccess) => {
            if (this.closeDialogFlag) {
                this.closeDialog(true);
            } else {
                this.data.contract = {...this.data.contract, ...action.payload};
                this.contractData = this.data.contract!.data!;
                this.loading$.next(false);
            }
        });
    }

    watchDeleteSuccessAction() {
        this.actions$.pipe(
            ofType(contractsActions.ContractsActionTypes.DeleteContractsSuccess),
            takeUntil(this.destroyed$)
        ).subscribe((action: contractsActions.DeleteContractsSuccess) => {
            if (action.payload == this.data.contract?.id) {
                this.closeDialog(false);
            }
        });
    }

    watchFailActions() {
        this.actions$.pipe(
            ofType(contractsActions.ContractsActionTypes.AddContractsFailure, contractsActions.ContractsActionTypes.EditContractsFailure),
            takeUntil(this.destroyed$)
        ).subscribe(() => {
            this.loading$.next(false);
        });
    }

    openItem(id: string, dataType: string) {
        if (!id) {
            return;
        }

        this.sharedService.openCrmItem(dataType, id);
    }

    tabChanged(event: any) {
        if (event.index == 1 && !this.contactsLoaded) {
            this.getContacts();
        }

        if (event.index == 2 && !this.filesLoaded) {
            this.getFiles();
        }

        if (event.index == 3 && !this.documentsLoaded) {
            this.getDocuments();
        }
    }

    getFiles() {
        if (!this.data.contract?.id) {
            return;
        }
        this.billingService.getContractFiles(this.data.contract.id).pipe(
            takeUntil(this.destroyed$),
            map((res: BillingContractDocumentModel[]) => {
                this.filesDataSource.data = res ? res : [];
            }),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.error.message}`
                );
                return EMPTY;
            }),
        ).subscribe();
    }

    getContacts() {
        if (!this.data.contract?.id) {
            return;
        }
        this.billingService.getContractContacts(this.data.contract.id).pipe(
            takeUntil(this.destroyed$),
            map((res: BillingContractContactModel[]) => {
                this.contactsDataSource.data = res ? res : [];
            }),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.error.message}`
                );
                return EMPTY;
            }),
        ).subscribe();
    }

    addContactFormToggle() {
        if (!this.addContactForm) {
            this.addContactForm = this.formBuilder.group({
                contact: ['', [Validators.required, ValidateIfObject]],
                position: [''],
                sms: [false],
                email: [true],
            });
        }

        this.addContactVisible = !this.addContactVisible;
    }

    saveContact() {
        const formData = this.addContactForm.getRawValue();

        if (!formData.contact?.id || !this.data?.contract?.id) {
            return;
        }

        this.loading$.next(true);

        const contactsToProceed = this.contactsDataSource.data;

        if (contactsToProceed.find(item => item.contact?.id === formData.contact.id)) {
            const contactToEditIndex = contactsToProceed.findIndex((item: BillingContractContactModel) => {
                return item.contact?.id === formData.contact.id;
            });

            if (contactToEditIndex !== -1) {
                contactsToProceed.splice(contactToEditIndex, 1, formData);
            }
        } else if (formData.contact?.id && formData.contact?.name) {
            contactsToProceed.unshift(formData);
        }

        this.proceedChangeContacts(contactsToProceed);
    }

    editContact(contact: BillingContractContactModel) {
        this.addContactFormToggle();

        this.addContactForm.patchValue({
            contact: {id: contact.contact?.id, name: contact.contact?.name},
            position: contact.position,
            sms: contact.sms,
            email: contact.email
        });
    }

    deleteContact(index: number) {
        const confirmDialog$ = this.sharedService.openDialog(
            ConfirmationDialogComponent,
            {...this.config.confirmationDialog, hasBackdrop: true},
            {
                title: SharedService.getTranslation(
                    this.translateService,
                    `Контакт буде видалено. Продовжити?`,
                ),
                cancelText: 'common.buttons.no',
                submitText: 'common.buttons.yes',
            }
        );

        confirmDialog$.afterClosed().pipe(
            take(1),
        ).subscribe((result) => {
            if (result) {
                const contactsToProceed = this.contactsDataSource.data;
                contactsToProceed.splice(index, 1);
                this.proceedChangeContacts(contactsToProceed);
            }
        });
    }

    proceedChangeContacts(contactsToProceed: BillingContractContactModel[]) {
        if (!this.data.contract?.id) {
            return;
        }

        this.billingService.setContractContacts(this.data.contract.id, contactsToProceed).pipe(
            takeUntil(this.destroyed$),
            map((res: BillingContractContactModel[]) => {
                this.contactsDataSource.data = res;
                this.addContactForm.reset();
                this.addContactVisible = false;
                this.loading$.next(false);
            }),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.error.message}`
                );
                this.loading$.next(false);
                return EMPTY;
            }),
        ).subscribe();
    }

    toggleContractFileSignedState(document: BillingContractDocumentModel) {
        const confirmDialog$ = this.sharedService.openDialog(
            ConfirmationDialogComponent,
            {...this.config.confirmationDialog, hasBackdrop: true},
            {
                title: SharedService.getTranslation(
                    this.translateService,
                    `Акт № ${document.name} буде помічено ${document.signedOffline ? 'як НЕ підписаний клієнтом та НЕ отриманий' : 'як підписаний клієнтом та отриманий'}. Продовжити?`,
                ),
                cancelText: 'common.buttons.no',
                submitText: 'common.buttons.yes',
            }
        );

        confirmDialog$.afterClosed().pipe(
            take(1),
        ).subscribe((result) => {
            if (result) {
                this.loading$.next(true);
                this.billingService.toggleContractFileSignedState(document.id!, !document.signedOffline).pipe(
                    takeUntil(this.destroyed$),
                    map(() => {
                        this.getFiles();
                        this.loading$.next(false);
                    }),
                    catchError((error: HttpErrorResponse) => {
                        SharedService.showErrorWithTitle(
                            this.translateService,
                            this.toastrService,
                            `responseErrors.${error.error.message}`
                        );
                        this.loading$.next(false);
                        return EMPTY;
                    }),
                ).subscribe();
            }
        });
    }

    getDocuments() {
        if (!this.data.contract?.id) {
            return;
        }

        this.billingService.listContractDocuments(this.data.contract.id, this.documentsPagination).pipe(
            takeUntil(this.destroyed$),
            map((res: PageBillingContractDocumentRowModel) => {
                this.documentsLoaded = true;
                this.documentsDataSource.data = res.content ? res.content : [];
                this.documentsPagination = {
                    ...this.documentsPagination,
                    number: res.number!,
                    size: res.size!,
                    totalElements: res.totalElements!,
                };
            }),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.error.message}`
                );
                return EMPTY;
            }),
        ).subscribe();

        this.billingService.loadDeletedActs(this.data.contract.id).pipe(
            takeUntil(this.destroyed$),
            map((res: BillingDeletedActModel[]) => {
                this.deletedActs = res;
            })
        ).subscribe();
    }

    changeDocumentsPage(event: any) {
        this.documentsPagination = {...this.documentsPagination, number: event.pageIndex, size: event.pageSize};
        this.getDocuments();
    }

    isAct(row: BillingContractDocumentRowModel): boolean {
        return row.type == 'ACT' || row.type == 'CUSTOM_ACT';
    }

    isTransaction(row: BillingContractDocumentRowModel): boolean {
        return row.type == 'PAYMENT' || row.type == 'REFUND';
    }

    /** The cards opened from the tab have no read-only mode, so the row needs the permission that edits its document. */
    canEditDocument(row: BillingContractDocumentRowModel): boolean {
        return this.isTransaction(row)
            ? this.checkPermissions('BILLING_NEW_TRANSACTION_EDIT')
            : this.checkPermissions('BILLING_NEW_ACT_EDIT');
    }

    documentTypeName(row: BillingContractDocumentRowModel): string {
        switch (row.type) {
            case 'INVOICE':
                return 'Рахунок';
            case 'ACT':
                return 'Акт';
            case 'CUSTOM_ACT':
                return 'Довільний акт';
            case 'PAYMENT':
                return 'Платіж';
            case 'REFUND':
                return 'Повернення';
            case 'ADJUSTMENT':
                return 'Коригування боргу';
            default:
                return '';
        }
    }

    /** «Додати рахунок» / «Додати акт» of the documents tab. */
    addDocument(documentType: 'INVOICE' | 'ACT') {
        const addDocumentDialog$ = this.sharedService.openDialog(
            AddDocumentDialogComponent,
            {...this.config.addItemDialog, width: '900px', hasBackdrop: true},
            {contract: this.data.contract, documentType}
        );

        addDocumentDialog$.afterClosed().pipe(
            take(1)
        ).subscribe((dialogData) => {
            if (dialogData?.result && this.data?.contract?.id) {
                this.store$.dispatch(new contractsActions.UpdateContract(this.data.contract.id));
                this.getDocuments();
            }
        });
    }

    /** «Підібрати платіж»: an unbound payment is found in the payments list and pinned to this contract. */
    pickPayment() {
        const listDialog$ = this.sharedService.openDialog(
            ListDialogComponent,
            {...this.config.itemsListDialog, hasBackdrop: true},
            {
                itemsType: ListItemType.TRANSACTION,
                props: {withContract: false, dahCounterParty: this.data.contract?.dahCounterParty}
            }
        );

        listDialog$.afterClosed().pipe(
            take(1)
        ).subscribe((dialogData) => {
            if (!dialogData?.data?.id) {
                return;
            }

            this.loading$.next(true);
            this.billingService.editPayment({
                id: dialogData.data.id,
                contract: {id: this.data.contract?.id, name: this.data.contract?.name}
            }).pipe(
                takeUntil(this.destroyed$),
                map(() => {
                    this.store$.dispatch(new contractsActions.UpdateContract(this.data.contract!.id!));
                    this.getDocuments();
                    this.loading$.next(false);
                }),
                catchError((error: HttpErrorResponse) => {
                    SharedService.showErrorWithTitle(
                        this.translateService,
                        this.toastrService,
                        `responseErrors.${error.error.message}`
                    );
                    this.loading$.next(false);
                    return EMPTY;
                }),
            ).subscribe();
        });
    }

    /** «Повернути кошти»: a payment order from the Dah account the contract is paid into. */
    refund() {
        const refundDialog$ = this.sharedService.openDialog(
            RefundDialogComponent,
            {...this.config.addItemDialog, width: '700px', hasBackdrop: true},
            {contractId: this.data.contract?.id, contractName: this.data.contract?.name}
        );

        refundDialog$.afterClosed().pipe(
            take(1)
        ).subscribe((dialogData) => {
            if (dialogData?.result && this.data?.contract?.id) {
                this.store$.dispatch(new contractsActions.UpdateContract(this.data.contract.id));
                this.getDocuments();
            }
        });
    }

    /** An act whose tax invoice 1C registered is reversed with a reason; everything else is simply deleted. */
    deleteDocument(row: BillingContractDocumentRowModel) {
        if (this.isAct(row) && row.taxInvoiceRegistered) {
            const reverseDialog$ = this.sharedService.openDialog(
                ReverseActDialogComponent,
                {...this.config.addItemDialog, width: '500px', hasBackdrop: true},
                {actName: row.name}
            );

            reverseDialog$.afterClosed().pipe(
                take(1),
            ).subscribe((dialogData) => {
                if (dialogData?.result) {
                    this.proceedDeleteDocument(row, dialogData.data);
                }
            });

            return;
        }

        const confirmDialog$ = this.sharedService.openDialog(
            ConfirmationDialogComponent,
            {...this.config.confirmationDialog, hasBackdrop: true},
            {
                title: SharedService.getTranslation(
                    this.translateService,
                    `${row.name} буде видалено. Продовжити?`,
                ),
                cancelText: 'common.buttons.no',
                submitText: 'common.buttons.yes',
            }
        );

        confirmDialog$.afterClosed().pipe(
            take(1),
        ).subscribe((result) => {
            if (result) {
                this.proceedDeleteDocument(row);
            }
        });
    }

    proceedDeleteDocument(row: BillingContractDocumentRowModel, reason?: string) {
        this.loading$.next(true);

        const request$ = this.isAct(row)
            ? this.billingService.deleteAct(row.id!, reason)
            : this.billingService.deleteInvoice(row.id!);

        request$.pipe(
            takeUntil(this.destroyed$),
            map(() => {
                this.store$.dispatch(new contractsActions.UpdateContract(this.data.contract!.id!));
                this.getDocuments();
                this.loading$.next(false);
            }),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.error.message}`
                );
                this.loading$.next(false);
                return EMPTY;
            }),
        ).subscribe();
    }

    signAct(row: BillingContractDocumentRowModel) {
        this.loading$.next(true);
        this.billingService.signAct(row.id!).pipe(
            takeUntil(this.destroyed$),
            map(() => {
                this.getDocuments();
                this.loading$.next(false);
            }),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.error.message}`
                );
                this.loading$.next(false);
                return EMPTY;
            }),
        ).subscribe();
    }

    toggleActSignedState(row: BillingContractDocumentRowModel) {
        const confirmDialog$ = this.sharedService.openDialog(
            ConfirmationDialogComponent,
            {...this.config.confirmationDialog, hasBackdrop: true},
            {
                title: SharedService.getTranslation(
                    this.translateService,
                    `${row.name} буде помічено ${row.signType == 'MANUALLY_SIGNED' ? 'як підписаний клієнтом та отриманий' : 'як НЕ підписаний клієнтом та НЕ отриманий'}. Продовжити?`,
                ),
                cancelText: 'common.buttons.no',
                submitText: 'common.buttons.yes',
            }
        );

        confirmDialog$.afterClosed().pipe(
            take(1),
        ).subscribe((result) => {
            if (result) {
                this.loading$.next(true);
                this.store$.dispatch(new actsActions.ToggleActSignedState({
                    act: {id: row.id, signType: row.signType},
                    signed: row.signType != 'MANUALLY_SIGNED'
                }));
            }
        });
    }

    /** Opens the card the row belongs to: an invoice, an act or the payment behind it. */
    openDocument(row: BillingContractDocumentRowModel) {
        const contract = {id: this.data.contract?.id, name: this.data.contract?.name};

        if (row.type == 'INVOICE') {
            this.openDialogAndReloadDocuments(InvoiceDialogComponent, {invoice: {id: row.id}, contract});
            return;
        }

        if (this.isAct(row)) {
            this.openDialogAndReloadDocuments(ActDialogComponent, {act: {id: row.id}, contract});
            return;
        }

        if (this.isTransaction(row)) {
            this.openDialogAndReloadDocuments(PaymentDialogComponent, {payment: {id: row.id}});
        }
    }

    private openDialogAndReloadDocuments(component: ComponentType<unknown>, dialogData: any) {
        const dialog$ = this.sharedService.openDialog(
            component,
            {...this.config.viewBillingDocDialog, hasBackdrop: true},
            dialogData
        );

        dialog$.afterClosed().pipe(
            take(1)
        ).subscribe(() => {
            this.getDocuments();
        });
    }

    downloadActFile(row: BillingContractDocumentRowModel, original: boolean) {
        this.loading$.next(true);
        this.billingService.downloadActFile(row.id!, original).pipe(
            takeUntil(this.destroyed$),
            map(res => {
                const disposition: string = <string>res.headers.get('X-DAH-Content-Disposition');
                fileSaver.saveAs(new Blob([<ArrayBuffer>res.body], {type: <string>res.headers.get('Content-Type')}), <string>Base64.decode(disposition));
                this.loading$.next(false);
            }),
            catchError(() => {
                this.loading$.next(false);
                return EMPTY;
            })
        ).subscribe();
    }

    openLink(row: BillingContractDocumentRowModel) {
        if (row.link) {
            window.open(row.link, '_blank');
        }
    }

    watchToggleActSignedStateSuccess() {
        this.actions$.pipe(
            ofType(actsActions.ActsActionTypes.ToggleActSignedStateSuccess),
            takeUntil(this.destroyed$)
        ).subscribe(() => {
            this.loading$.next(false);
            this.getDocuments();
        });
    }

    minimize() {
        SharedService.minimize(
            this.dialogRef.id,
            `${this.data.contract?.counterParty?.name ? this.data.contract?.counterParty?.name : this.data.contract?.organization?.name}`,
            this.store$,
            this.data.contract?.id,
            false,
            '',
            `Договір - ${this.data.contract?.counterParty?.name ? this.data.contract?.counterParty?.name : this.data.contract?.organization?.name}`,
        );
    }

    nonManagersToggle() {
        if (!this.selectedContractData.data?.date) {
            return;
        }

        this.loading$.next(true);
        this.billingService.nonManagersToggle(this.data.contract!.id!, this.selectedContractData.data.date, !this.selectedContractData.data.managerIgnore).pipe(
            takeUntil(this.destroyed$),
            map(() => {
                this.selectedContractData = {...this.selectedContractData, data: {...this.selectedContractData.data, managerIgnore: !this.selectedContractData.data.managerIgnore}};

                const data =  this.data.contract!.data!;
                const index = data.findIndex(el => el.date == this.selectedContractData.data.date);
                data.splice(index!, 1, this.selectedContractData.data);
                this.data.contract = {...this.data.contract, data};

                this.loading$.next(false);
            }),
            catchError(() => {
                this.loading$.next(false);
                return EMPTY;
            })
        ).subscribe();
    }

    closeDialog(result: boolean, data?: any) {
        this.dialogRef.close({result, data});
    }
}
